import React from 'react';
import { cn } from '@/utils';
import { TelegramUtils } from '@/utils/telegram';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  haptic?: boolean;
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'primary', 
    size = 'md', 
    loading = false, 
    haptic = true,
    disabled,
    onClick,
    children, 
    ...props 
  }, ref) => {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (loading || disabled) return;
      
      // Haptic feedback
      if (haptic) {
        if (variant === 'danger') {
          TelegramUtils.haptic.medium();
        } else if (variant === 'primary' || variant === 'success') {
          TelegramUtils.haptic.light();
        } else {
          TelegramUtils.haptic.selection();
        }
      }
      
      onClick?.(event);
    };

    return (
      <button
        className={cn(
          // Base styles
          'inline-flex items-center justify-center rounded-xl font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-tg-bg disabled:opacity-50 disabled:cursor-not-allowed tap-highlight-transparent active:scale-95',
          
          // Variants
          {
            'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500': variant === 'primary',
            'bg-tg-secondary-bg text-tg-text border border-neutral-700 hover:bg-neutral-800 focus:ring-neutral-500': variant === 'secondary',
            'bg-success-500 text-white hover:bg-success-600 focus:ring-success-500': variant === 'success',
            'bg-danger-500 text-white hover:bg-danger-600 focus:ring-danger-500': variant === 'danger',
            'bg-transparent text-tg-text hover:bg-neutral-800 focus:ring-neutral-500': variant === 'ghost',
          },
          
          // Sizes
          {
            'px-3 py-2 text-sm min-h-[36px]': size === 'sm',
            'px-4 py-3 text-base min-h-[44px]': size === 'md',
            'px-6 py-4 text-lg min-h-[52px]': size === 'lg',
            'w-12 h-12 p-0 rounded-full': size === 'icon',
          },
          
          // Loading state
          {
            'cursor-not-allowed': loading,
          },
          
          className
        )}
        disabled={disabled || loading}
        onClick={handleClick}
        ref={ref}
        {...props}
      >
        {loading && (
          <div className="mr-2 w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
